import { useId } from 'react';

import { OptionTile } from './OptionTile';
import { useRovingRadioGroup } from '../../hooks/useRovingRadioGroup';

export interface TileOption<T extends string> {
  value: T;
  label: string;
  description?: string;
}

export interface TileRadioGroupProps<T extends string> {
  /** The group's accessible name. Rendered visibly unless `labelHidden`. */
  label: string;
  /** For surfaces that already carry their own heading for this question. */
  labelHidden?: boolean;
  options: TileOption<T>[];
  value?: T;
  onChange: (value: T) => void;
  /** Columns at `sm` and up; the grid is always single-column below that. */
  columns: 2 | 3;
  /** Id of an external error message to expose as the group's description. */
  errorId?: string;
  /** Gives the caller a handle for moving focus into the group. */
  containerRef?: React.Ref<HTMLDivElement>;
}

const COLUMN_CLASS: Record<2 | 3, string> = {
  2: 'grid grid-cols-1 gap-3 sm:grid-cols-2',
  3: 'grid grid-cols-1 gap-3 sm:grid-cols-3',
};

/**
 * A single-choice grid of `OptionTile`s, wired as a real radiogroup.
 *
 * Every caller used to hand-roll this: a bare `<div className='grid …'>` of
 * `aria-pressed` buttons with an unassociated `<h3>` above it. That cost
 * keyboard users arrow-key navigation (you tabbed through all ten tiles), and
 * cost screen-reader users both the group name and the "n of m" position.
 *
 * The sibling of `SegmentedControl` — same WAI-ARIA radiogroup pattern and the
 * same roving-tabindex machinery, deliberately kept in step. Reach for this one
 * when the options need a description line and room to breathe, and for
 * `SegmentedControl` when they are one-word switches in a compact track.
 *
 * Selection follows focus, which is the standard pattern for a radiogroup with
 * no expensive side effects — callers here just set local state.
 */
export function TileRadioGroup<T extends string>({
  label,
  labelHidden = false,
  options,
  value,
  onChange,
  columns,
  errorId,
  containerRef,
}: TileRadioGroupProps<T>) {
  const labelId = useId();
  // `select` and `selectedIndex` stay inside the hook: the click path goes
  // straight to `onChange` (a pointer user is already where they want to be, so
  // there is nothing to move focus to), and selection is read off `value`.
  const {
    items: tiles,
    tabbableIndex,
    handleKeyDown,
  } = useRovingRadioGroup({ options, value, onChange });

  return (
    <>
      {/* `text-subhead`, not `text-sm font-medium`: the question and its option
          titles were both 14px and the OPTION was the heavier of the two, so
          the answer outranked the question asking it. */}
      {!labelHidden && (
        <h3 id={labelId} className='text-subhead text-foreground mb-4'>
          {label}
        </h3>
      )}
      <div
        ref={containerRef}
        role='radiogroup'
        aria-label={labelHidden ? label : undefined}
        aria-labelledby={labelHidden ? undefined : labelId}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        className={COLUMN_CLASS[columns]}
      >
        {options.map((option, index) => (
          <OptionTile
            key={option.value}
            ref={el => {
              tiles.current[index] = el;
            }}
            title={option.label}
            subtitle={option.description}
            selected={value === option.value}
            tabIndex={index === tabbableIndex ? 0 : -1}
            onSelect={() => onChange(option.value)}
            onKeyDown={event => handleKeyDown(event, index)}
          />
        ))}
      </div>
    </>
  );
}
